import { DatePicker, Radio } from 'antd'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import styles from './charts.module.scss'

const { RangePicker } = DatePicker

// The hook stores native UTC Dates; antd v4 speaks moment. Convert at the boundary.
const toMoment = value => (moment.isMoment(value) ? value : moment.utc(value))

const rangeToPickerValue = range =>
  range ? [toMoment(range[0]), toMoment(range[1])] : null

/**
 * Shared range selector for one dashboard chart.
 *
 * Renders a granularity toggle only when the chart's payload actually carries more
 * than one granularity (today that is Logins por rol alone); every other chart gets
 * just the date-window picker. The component is stateless — all range state lives in
 * the `useChartRange` instance owned by the chart, which keeps each chart's range
 * independent and session-only.
 */
export const ChartRangeControl = ({
  range,
  onRangeChange,
  disabledDate,
  granularities = [],
  granularity = 'months',
  onGranularityChange
}) => {
  const { t } = useTranslation()

  if (!range) return null

  const showGranularityToggle = Array.isArray(granularities) && granularities.length > 1

  return (
    <div className={styles.range_controls}>
      {showGranularityToggle && (
        <Radio.Group
          size='small'
          value={granularity}
          onChange={event => onGranularityChange?.(event.target.value)}
        >
          {granularities.map(value => (
            <Radio.Button key={value} value={value}>
              {t(`pages.dashboard.usability.timeRanges.${value}`)}
            </Radio.Button>
          ))}
        </Radio.Group>
      )}
      <RangePicker
        size='small'
        value={rangeToPickerValue(range)}
        onChange={dates => {
          if (!dates || !dates[0] || !dates[1]) return
          onRangeChange?.(dates)
        }}
        format='DD/MM/YYYY'
        allowClear={false}
        disabledDate={disabledDate}
        placeholder={[
          t('pages.dashboard.usability.dateRange.start'),
          t('pages.dashboard.usability.dateRange.end')
        ]}
      />
    </div>
  )
}

/**
 * Footer line under a chart: the visible window plus the clamp hint. Extracted so
 * all charts describe their range the same way instead of re-assembling the string.
 */
export const ChartRangeSummary = ({ range, hint }) => {
  const { t } = useTranslation()

  if (!range) return null

  return (
    <div className={styles.range_summary}>
      <span className={styles.range_summary_label}>
        {t('pages.dashboard.usability.visibleRange')}:{' '}
        {toMoment(range[0]).format('DD/MM/YYYY')} - {toMoment(range[1]).format('DD/MM/YYYY')}
      </span>
      {hint && <span className={styles.range_summary_hint}>{hint}</span>}
    </div>
  )
}

export default ChartRangeControl
